import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor/RichTextEditor";
import { ColumnsEditorProps } from "./ColumnsEditor.types";
import { ColumnsBlockContent } from "@/modules/hub/features/ResourceManagement/components/SortableBlock/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export function ColumnsEditor({ value, onChange }: ColumnsEditorProps) {
  const [leftColumn, setLeftColumn] = useState("");
  const [rightColumn, setRightColumn] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (value && !isInitialized) {
      try {
        const parsed = JSON.parse(value) as ColumnsBlockContent;
        setLeftColumn(parsed.leftColumn || "");
        setRightColumn(parsed.rightColumn || "");
        setIsInitialized(true);
      } catch (e) {
        console.error("Failed to parse columns content:", e);
        setLeftColumn("");
        setRightColumn("");
      }
    }
  }, [value, isInitialized]);

  const updateContent = (left: string, right: string) => {
    const content: ColumnsBlockContent = {
      leftColumn: left,
      rightColumn: right,
    };
    onChange(JSON.stringify(content));
  };

  const handleLeftChange = (content: string) => {
    setLeftColumn(content);
    updateContent(content, rightColumn);
  };

  const handleRightChange = (content: string) => {
    setRightColumn(content);
    updateContent(leftColumn, content);
  };

  return (
    <div className="space-y-4">
      <Tabs className="w-full" defaultValue="edit">
        <TabsList>
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent className="space-y-4" value="edit">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Left Column</Label>
              <div className="border border-gray-200 dark:border-slate-700 rounded-md p-2 min-h-[200px]">
                <RichTextEditor
                  id="columns-left"
                  initialContent={leftColumn}
                  key={`left-${isInitialized}`}
                  onChange={handleLeftChange}
                  onRemove={() => {}}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Right Column</Label>
              <div className="border border-gray-200 dark:border-slate-700 rounded-md p-2 min-h-[200px]">
                <RichTextEditor
                  id="columns-right"
                  initialContent={rightColumn}
                  key={`right-${isInitialized}`}
                  onChange={handleRightChange}
                  onRemove={() => {}}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 bg-muted/50 rounded-lg">
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {leftColumn || "*Left column content will appear here*"}
              </ReactMarkdown>
            </div>
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {rightColumn || "*Right column content will appear here*"}
              </ReactMarkdown>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            On mobile devices, these columns will stack vertically.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
